import Button from "@mui/material/Button";
import { FormDialog, selectOptionSchema } from "components/common/FormDialog";
import { FIELD_TYPES, SelectOption } from "components/common/FormDialog/types";
import {
  backupStorageLinkResultSelector,
  backupStoragePickerSelector
} from "modules/databases/selectors";
import { databasesActions } from "modules/databases/slices";
import { BackupStorageContext } from "modules/databases/types";
import { FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Schema, string } from "yup";
import { ERROR_MESSAGES, REGEX } from "../../constants";

const PROJECT = { label: "This project's object storage", value: "project" };
const MANUAL = { label: "External S3 storage", value: "manual" };
type StorageLink = {
  endpoint: string;
  bucket: string;
  key: string;
  secret: string;
};
type LinkForm = StorageLink & {
  source: SelectOption;
  credentials?: SelectOption;
  projectBucket?: SelectOption;
};

export const S3LinkDialog: FC<{
  isOpened: boolean;
  regionId: string;
  projectId: string;
  onCancel: () => void;
  onConfirm: (storage: StorageLink) => void;
}> = ({ isOpened, regionId, projectId, onCancel, onConfirm }) => {
  const dispatch = useDispatch();
  const storage = useSelector(backupStoragePickerSelector);
  const link = useSelector(backupStorageLinkResultSelector);
  const [pickerContext, setPickerContext] =
    useState<BackupStorageContext | null>(null);
  const [source, setSource] = useState(PROJECT.value);
  const [credentialId, setCredentialId] = useState("");
  const [bucketName, setBucketName] = useState("");
  const [creating, setCreating] = useState<"key" | "bucket" | null>(null);
  const submittedBucket = useRef("");
  const submittedKey = useRef(false);
  const submittedLink = useRef(false);
  const activeSession = useRef<string | null>(null);
  const matchesProject = (
    context: { regionId: string; projId: string } | null | undefined
  ) => context?.regionId === regionId && context.projId === projectId;
  const credentials = matchesProject(storage.credentialsListContext)
    ? (storage.credentialsList ?? [])
    : [];
  const createdKey = matchesProject(storage.createdCredentials?.params)
    ? storage.createdCredentials?.credentials
    : undefined;
  const keyOptions = [...credentials];
  if (createdKey && !keyOptions.some((key) => key.id === createdKey.id))
    keyOptions.push(createdKey);
  const fetchedDetails =
    matchesProject(storage.credentialsContext) &&
    storage.credentials?.id === credentialId
      ? storage.credentials
      : undefined;
  const details = fetchedDetails?.access_secret
    ? fetchedDetails
    : createdKey?.id === credentialId && createdKey.access_secret
      ? createdKey
      : fetchedDetails;
  const bucketsMatch =
    matchesProject(storage.bucketsContext) &&
    storage.bucketsContext?.credsId === credentialId;
  const buckets = bucketsMatch ? (storage.buckets ?? []) : [];
  const bucketsLoading =
    !storage.bucketsError && (!bucketsMatch || storage.bucketsLoading);
  const newBucket =
    matchesProject(storage.createdBucket?.params) &&
    storage.createdBucket?.params.credsId === credentialId
      ? storage.createdBucket.params.data.name
      : undefined;
  const bucketOptions = buckets.map(({ name }) => ({
    label: name,
    value: name
  }));
  if (newBucket && !bucketOptions.some(({ value }) => value === newBucket))
    bucketOptions.push({ label: newBucket, value: newBucket });
  const creatingResource =
    storage.credentialsCreating || storage.bucketCreating;

  const loadKeys = () => {
    if (pickerContext)
      dispatch(databasesActions.backupStorageKeysRequest(pickerContext));
  };
  const loadBuckets = () => {
    if (credentialId && pickerContext)
      dispatch(
        databasesActions.backupStorageBucketsRequest({
          ...pickerContext,
          credsId: credentialId
        })
      );
  };

  useEffect(() => {
    if (!isOpened) return;
    setSource(PROJECT.value);
    setCredentialId("");
    setBucketName("");
    setCreating(null);
    submittedLink.current = false;
    dispatch(databasesActions.clearS3LinkResult());
    const context = {
      regionId,
      projId: projectId,
      sessionId: crypto.randomUUID()
    };
    activeSession.current = context.sessionId;
    setPickerContext(context);
    dispatch(databasesActions.openBackupStoragePicker(context));
    dispatch(databasesActions.backupStorageKeysRequest(context));
    return () => {
      if (activeSession.current === context.sessionId)
        activeSession.current = null;
      dispatch(databasesActions.closeBackupStoragePicker(context));
    };
  }, [dispatch, isOpened, regionId, projectId]);

  useEffect(() => {
    if (
      !isOpened ||
      !credentialId ||
      !pickerContext ||
      pickerContext.sessionId !== activeSession.current
    )
      return;
    dispatch(
      databasesActions.backupStorageKeyRequest({
        ...pickerContext,
        credsId: credentialId
      })
    );
    dispatch(
      databasesActions.backupStorageBucketsRequest({
        ...pickerContext,
        credsId: credentialId
      })
    );
  }, [dispatch, isOpened, credentialId, pickerContext]);

  useEffect(() => {
    if (!isOpened || creating !== "key" || !submittedKey.current || !createdKey)
      return;
    setCredentialId(createdKey.id);
    setBucketName("");
    setCreating(null);
    submittedKey.current = false;
  }, [isOpened, creating, createdKey]);

  useEffect(() => {
    if (
      !isOpened ||
      creating !== "bucket" ||
      !newBucket ||
      newBucket !== submittedBucket.current
    )
      return;
    setBucketName(newBucket);
    setCreating(null);
  }, [isOpened, creating, newBucket]);

  useEffect(() => {
    if (isOpened && submittedLink.current && link.successful) {
      submittedLink.current = false;
      onCancel();
    }
  }, [isOpened, link.successful, onCancel]);

  const manualRule = (rule = string()) =>
    rule.when("source", {
      is: (value: SelectOption) => value?.value === MANUAL.value,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    });
  const projectRule = (
    selectOptionSchema.default(undefined) as Schema<SelectOption>
  ).when("source", {
    is: (value: SelectOption) => value?.value === PROJECT.value,
    then: (schema: Schema<SelectOption>) =>
      schema.required() as Schema<SelectOption>,
    otherwise: (schema: Schema<SelectOption>) =>
      schema.notRequired() as Schema<SelectOption>
  });
  const projectOnly = (values: Record<string, unknown>) =>
    (values.source as SelectOption)?.value !== PROJECT.value;
  const manualOnly = (values: Record<string, unknown>) =>
    (values.source as SelectOption)?.value !== MANUAL.value;
  const missingSecret =
    credentialId &&
    !storage.credentialsLoading &&
    details &&
    !details.access_secret
      ? "This access key's secret is unavailable. Retry reading the key or choose another access key."
      : null;
  const error =
    storage.credentialsListError ||
    storage.credentialsError ||
    (bucketsMatch && storage.bucketsError) ||
    missingSecret;

  return (
    <>
      <FormDialog
        isOpened={isOpened && creating === null}
        onCancel={onCancel}
        title="Connect backup storage"
        confirmButtonLabel={link.loading ? "Connecting..." : "Connect storage"}
        onConfirm={(data: LinkForm) => {
          if (link.loading || creatingResource) return;
          const payload =
            data.source.value === PROJECT.value
              ? details?.access_secret &&
                details.id === data.credentials?.value &&
                data.projectBucket?.value
                ? {
                    endpoint: details.endpoint,
                    bucket: data.projectBucket.value,
                    key: details.access_key,
                    secret: details.access_secret
                  }
                : null
              : {
                  endpoint: data.endpoint.trim(),
                  bucket: data.bucket.trim(),
                  key: data.key.trim(),
                  secret: data.secret
                };
          if (!payload) return;
          submittedLink.current = true;
          onConfirm(payload);
        }}
        fields={[
          {
            name: "source",
            label: "Storage provider",
            type: FIELD_TYPES.SELECT,
            options: [PROJECT, MANUAL],
            defaultValue: source === MANUAL.value ? MANUAL : PROJECT,
            rules: selectOptionSchema,
            onChange: ({ value }) =>
              setSource((value as SelectOption | null)?.value ?? PROJECT.value)
          },
          {
            name: "credentials",
            label: "Access key",
            type: FIELD_TYPES.SELECT,
            defaultValue: keyOptions.some((key) => key.id === credentialId)
              ? {
                  label: keyOptions.find((key) => key.id === credentialId)!
                    .access_key,
                  value: credentialId
                }
              : null,
            isHidden: projectOnly,
            options: keyOptions.map((key) => ({
              label: `${key.access_key} (${key.endpoint})`,
              value: key.id
            })),
            rules: projectRule,
            helperText: storage.credentialsListLoading ? (
              "Loading access keys..."
            ) : storage.credentialsListError ? (
              <Button size="small" onClick={loadKeys}>
                Retry loading access keys
              </Button>
            ) : storage.credentialsError || missingSecret ? (
              <Button
                size="small"
                onClick={() => {
                  if (pickerContext && credentialId)
                    dispatch(
                      databasesActions.backupStorageKeyRequest({
                        ...pickerContext,
                        credsId: credentialId
                      })
                    );
                }}
              >
                Retry reading access key
              </Button>
            ) : keyOptions.length === 0 ? (
              "No access keys yet. Create one from the list."
            ) : (
              "Choose an access key to load its buckets."
            ),
            dependent_fields: ["projectBucket"],
            onChange: ({ value }) => {
              setCredentialId((value as SelectOption | null)?.value ?? "");
              setBucketName("");
            },
            // FormDialog has no loading row: the create row stands in for one, inert until the list arrives.
            onCreate: storage.credentialsListLoading
              ? () => {}
              : () => {
                  submittedKey.current = false;
                  setCreating("key");
                },
            createText: storage.credentialsListLoading
              ? "Loading access keys..."
              : "Create access key"
          },
          {
            name: "projectBucket",
            label: "Backup bucket",
            type: FIELD_TYPES.SELECT,
            defaultValue: bucketName
              ? { label: bucketName, value: bucketName }
              : null,
            isHidden: (values) => projectOnly(values) || !values.credentials,
            options: bucketOptions,
            rules: projectRule,
            helperText: storage.bucketsError ? (
              <Button size="small" onClick={loadBuckets}>
                Retry loading buckets
              </Button>
            ) : bucketsLoading ? (
              "Loading buckets..."
            ) : bucketOptions.length === 0 ? (
              "No buckets yet. Create a backup bucket from the list."
            ) : (
              "New buckets are selected automatically after creation."
            ),
            onChange: ({ value }) =>
              setBucketName((value as SelectOption | null)?.value ?? ""),
            onCreate: bucketsLoading
              ? () => {}
              : credentialId
                ? () => {
                    submittedBucket.current = "";
                    setCreating("bucket");
                  }
                : undefined,
            createText: bucketsLoading
              ? "Loading buckets..."
              : "Create backup bucket"
          },
          {
            name: "storageError",
            type: FIELD_TYPES.NOTES,
            label: error ?? "",
            isHidden: (values) => projectOnly(values) || !error
          },
          {
            name: "endpoint",
            label: "S3 endpoint",
            type: FIELD_TYPES.TEXT,
            isHidden: manualOnly,
            rules: manualRule().matches(REGEX.HTTP_HTTPS_ADDRESS, {
              message: ERROR_MESSAGES.HTTP_HTTPS_ADDRESS,
              excludeEmptyString: true
            })
          },
          {
            name: "bucket",
            label: "Bucket name",
            type: FIELD_TYPES.TEXT,
            isHidden: manualOnly,
            rules: manualRule().matches(REGEX.BUCKET_NAME, {
              message: ERROR_MESSAGES.BUCKET_NAME,
              excludeEmptyString: true
            })
          },
          {
            name: "key",
            label: "Access key",
            type: FIELD_TYPES.TEXT,
            isHidden: manualOnly,
            rules: manualRule()
          },
          {
            name: "secret",
            label: "Secret key",
            type: FIELD_TYPES.PASSWORD,
            isHidden: manualOnly,
            rules: manualRule()
          },
          {
            name: "linkError",
            label: link.error ?? "",
            type: FIELD_TYPES.NOTES,
            isHidden: () => !link.error
          }
        ]}
      />
      <FormDialog
        isOpened={creating === "key"}
        onCancel={() => setCreating(null)}
        title="Create an object storage access key"
        confirmButtonLabel={
          storage.credentialsCreating ? "Creating..." : "Create access key"
        }
        isLocked={storage.credentialsCreating}
        onConfirm={() => {
          if (pickerContext && !storage.credentialsCreating) {
            submittedKey.current = true;
            dispatch(
              databasesActions.createBackupStorageKeyRequest(pickerContext)
            );
          }
        }}
        fields={[
          {
            name: "info",
            type: FIELD_TYPES.NOTES,
            label:
              storage.createCredentialsError ||
              "The new access key will be selected for your backup storage."
          }
        ]}
      />
      <FormDialog
        isOpened={creating === "bucket"}
        onCancel={() => setCreating(null)}
        title="Create a backup bucket"
        confirmButtonLabel={
          storage.bucketCreating ? "Creating..." : "Create bucket"
        }
        isLocked={storage.bucketCreating}
        onConfirm={(data: { name: string }) => {
          if (!credentialId || !pickerContext || storage.bucketCreating) return;
          submittedBucket.current = data.name;
          dispatch(
            databasesActions.createBackupStorageBucketRequest({
              ...pickerContext,
              credsId: credentialId,
              name: data.name
            })
          );
        }}
        fields={[
          {
            name: "name",
            label: "Bucket name",
            type: FIELD_TYPES.TEXT,
            rules: string()
              .required()
              .matches(REGEX.BUCKET_NAME, ERROR_MESSAGES.BUCKET_NAME)
              .min(3, ERROR_MESSAGES.MIN_BUCKET_NAME)
              .max(63, ERROR_MESSAGES.MAX_BUCKET_NAME)
              .test(
                "sameNameCheckFormat",
                "This name already exists",
                (value) =>
                  !bucketOptions.some((bucket) => bucket.value === value)
              )
          },
          {
            name: "error",
            type: FIELD_TYPES.NOTES,
            label: storage.createBucketError ?? "",
            isHidden: () => !storage.createBucketError
          }
        ]}
      />
    </>
  );
};
